import { ProjectKind } from "@/generated/graphql";
import { useProducts } from "@/lib/hooks/products/useProducts";
import { useAddProject } from "@/lib/hooks/projects/useAddProject";
import { useProjectsById } from "@/lib/hooks/projects/useProjectsById";
import { productLevelLabel, productLevelOptions } from "@/lib/utils/productLevel";
import { scrollToItem } from "@/lib/utils/scrollToItem";
import { Button, FormField, Modal } from "@ai-teach/ui-web";
import { useState } from "react";
import toast from "react-hot-toast";

/** No level chosen yet. Not 0 — that is a real level on products with a base level. */
const UNSET_LEVEL = -1;

interface AddAudioFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  parentId: string | null;
}

/**
 * Creates an audio folder — a project that holds audio files instead of lessons.
 *
 * Deliberately much smaller than AddProjectModal: an audio folder has no thumbnail to
 * upload and no hidden flag to set at creation time. Under a parent it inherits product
 * and level from that parent — the same fields AddProjectModal inherits — so the teacher
 * only types a name. At root there is nothing to inherit, so both have to be picked.
 *
 * Access control is left at the AddProjectModal default (all schools) and changed
 * afterwards through Edit, rather than duplicating that whole section here.
 */
export function AddAudioFolderModal({ isOpen, onClose, parentId }: AddAudioFolderModalProps) {
  const [name, setName] = useState("");
  const [productId, setProductId] = useState("");
  const [level, setLevel] = useState(UNSET_LEVEL);
  const [errors, setErrors] = useState({ name: false, productId: false, level: false });

  const { addProject, loading } = useAddProject();
  const { project: parentProject, loading: parentLoading } = useProjectsById(parentId);
  const { products, loading: productsLoading } = useProducts("");

  const isRoot = parentId === null;
  const selectedProduct = products.find((product) => product.id === productId);

  // Under a parent every access setting is inherited, so the form has nothing to ask for.
  // A root folder is created with one product; further products are added through Edit.
  const effectiveProductIds = isRoot
    ? productId
      ? [productId]
      : []
    : (parentProject?.productIds ?? []);
  const effectiveLevel = isRoot ? level : (parentProject?.level ?? UNSET_LEVEL);

  const handleClose = () => {
    setName("");
    setProductId("");
    setLevel(UNSET_LEVEL);
    setErrors({ name: false, productId: false, level: false });
    onClose();
  };

  const handleSubmit = async () => {
    const nextErrors = {
      name: !name.trim(),
      productId: effectiveProductIds.length === 0,
      level: effectiveLevel === UNSET_LEVEL,
    };
    setErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean)) return;

    try {
      const created = await addProject({
        name: name.trim(),
        parentId,
        productIds: effectiveProductIds,
        level: effectiveLevel,
        isPublic: true,
        schoolIds: [],
        isHidden: false,
        kind: ProjectKind.Audio,
      });
      if (created?.[0]) scrollToItem("project", created[0].id);
      handleClose();
    } catch (error) {
      console.error("Failed to add audio folder:", error);
      toast.error("Failed to add audio folder. Please try again.");
    }
  };

  const isBusy = loading || parentLoading;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Audio Folder">
      <div className="space-y-6">
        <p className="text-sm text-secondary-200">
          An audio folder holds audio files only. It cannot contain projects or lessons.
        </p>

        <FormField
          id="audioFolderName"
          name="audioFolderName"
          label="Name"
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (errors.name) setErrors((prev) => ({ ...prev, name: false }));
          }}
          required
          placeholder="Enter audio folder name"
          error={errors.name ? " " : undefined}
        />

        {isRoot && (
          <>
            <div>
              <label htmlFor="audioFolderProduct" className="block label mb-1">
                <span className="text-red-500">*</span> Product
              </label>
              <select
                id="audioFolderProduct"
                value={productId}
                onChange={(e) => {
                  setProductId(e.target.value);
                  setLevel(UNSET_LEVEL);
                  if (errors.productId) setErrors((prev) => ({ ...prev, productId: false }));
                }}
                disabled={productsLoading}
                className={`mt-1 block w-full px-3 py-2 bg-surface border rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer ${
                  errors.productId ? "border-red-500" : "border-gray-800"
                }`}
              >
                <option value="">{productsLoading ? "Loading..." : "Select product"}</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedProduct && (
              <div>
                <label htmlFor="audioFolderLevel" className="block label mb-1">
                  <span className="text-red-500">*</span> Level
                </label>
                <select
                  id="audioFolderLevel"
                  value={level === UNSET_LEVEL ? "" : level}
                  onChange={(e) => {
                    setLevel(Number(e.target.value));
                    if (errors.level) setErrors((prev) => ({ ...prev, level: false }));
                  }}
                  className={`mt-1 block w-full px-3 py-2 bg-surface border rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer ${
                    errors.level ? "border-red-500" : "border-gray-800"
                  }`}
                >
                  <option value="">Select level</option>
                  {productLevelOptions(selectedProduct).map((levelOption) => (
                    <option key={`level${levelOption}`} value={levelOption}>
                      {productLevelLabel(selectedProduct, levelOption)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        <div className="flex justify-end space-x-3 pt-2">
          <Button variant="secondary" onClick={handleClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isBusy}>
            {loading ? "Adding..." : "Add"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
